import { ApiError } from "@/types";
import { state } from "@/state";

/** API 请求选项,headers 使用 Record 便于合并 */
export type ApiOptions = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

/**
 * 统一 API 客户端
 * - 自动携带 credentials: include
 * - 不安全方法自动加 CSRF 头
 * - 失败时抛出 ApiError
 */
export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = (options.method || "GET").toUpperCase();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(options.headers || {})
  };
  // 不安全方法加 CSRF 头
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && state.csrfToken) {
    headers["x-csrf-token"] = state.csrfToken;
  }

  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers
  });

  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  let data: unknown = {};
  if (text) {
    if (!contentType.includes("application/json")) {
      throw new ApiError(
        `服务返回了非 JSON 响应(${response.status}),请检查后端服务或 nginx 反代配置`,
        response.status,
        "non_json_response",
        {
          contentType,
          snippet: text.replace(/\s+/g, " ").trim().slice(0, 200)
        }
      );
    }
    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new ApiError(
        "服务返回的 JSON 格式异常",
        response.status,
        "invalid_json_response",
        { message: error instanceof Error ? error.message : String(error) }
      );
    }
  }

  if (!response.ok) {
    const errBody = (data as { error?: { message?: string; code?: string } }).error;
    throw new ApiError(
      errBody?.message || `Request failed: ${response.status}`,
      response.status,
      errBody?.code,
      errBody as Record<string, unknown> | undefined
    );
  }

  return data as T;
}
